import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Flex, Icon, Modal, Typography } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import { useAuthState } from '@percent/cause-dashboard/common/hooks/useAuthState/useAuthState'
import { useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics'
import { usePostHogFeatureFlagVariant } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { GrantQuestionnaireExperiment } from './GrantQuestionnaireExperiment'
import { GrantEligibilityQuestionnaire } from './GrantEligibilityQuestionnaire'
import { SorryScreen } from './SorryScreen'
import { BookingNotice } from './BookingNotice'
import { SupportRedirectScreen } from './SupportRedirectScreen'
import {
  GRANT_ADVISORY_BOOKING_EVENTS,
  GRANT_ADVISORY_QUESTIONNAIRE_EVENTS,
  GRANT_ADVISORY_SOURCE,
  GrantAdvisoryEntryPoint,
  QuestionnaireNonBlockingVariant,
  QuestionnaireQuestion,
  SupportLinkPlacement
} from './grantAdvisoryBooking.utils'
import styles from './GrantAdvisoryBookingModal.module.scss'

type GrantAdvisoryBookingModalProps = {
  onClose: () => void
  entryPoint: GrantAdvisoryEntryPoint
  // Resolved by the caller via `useGrantAdvisoryBookingUrl`. Callers only render
  // this modal once a URL exists, so the booking CTA always has somewhere to go.
  bookingUrl: string
  onScheduled?: () => void
  source?: string
  analyticsProperties?: Record<string, unknown>
}

// `support`: the user came with a support query, not for AgentPro.
type QuestionnaireProgress = 'in_progress' | 'qualified' | 'disqualified' | 'support'

// `experiment` is Smart Apply's flag-gated questionnaire; `eligibility` the sidebar's Yes/No check.
type QuestionnaireKind = 'experiment' | 'eligibility'

type BookingModalProps = GrantAdvisoryBookingModalProps & {
  // Undefined skips straight to the booking CTA.
  questionnaire?: QuestionnaireKind
  questionnaireVariant?: string | boolean
}

export const GrantAdvisoryBookingModal = (props: GrantAdvisoryBookingModalProps) =>
  props.entryPoint === 'sidebar' ? (
    <BookingModal {...props} questionnaire="eligibility" />
  ) : (
    <SmartApplyBookingModal {...props} />
  )

/*
  Only Smart Apply reads the questionnaire experiment flag: reading a PostHog flag reports
  an exposure, and the sidebar always shows the eligibility check, so enrolling it would
  dilute the readout.
*/
const SmartApplyBookingModal = (props: GrantAdvisoryBookingModalProps) => {
  const questionnaireVariant = usePostHogFeatureFlagVariant(POSTHOG_FEATURE_FLAGS.CP_GRANT_ADVISORY_QUESTIONNAIRE)

  return (
    <BookingModal
      {...props}
      questionnaire={questionnaireVariant === 'test' ? 'experiment' : undefined}
      questionnaireVariant={questionnaireVariant}
    />
  )
}

const BookingModal = ({
  onClose,
  entryPoint,
  bookingUrl,
  onScheduled,
  source = GRANT_ADVISORY_SOURCE,
  analyticsProperties,
  questionnaire,
  questionnaireVariant
}: BookingModalProps) => {
  const { t } = useTranslation()
  const { authState } = useAuthState()
  const { track } = useCausesPortalAnalytics()

  const [progress, setProgress] = useState<QuestionnaireProgress>('in_progress')

  // Set on questionnaire mount, long before booking is reachable. Undefined for the
  // outer control cohort and the sidebar, neither of which is enrolled in this experiment.
  const [nonBlockingVariant, setNonBlockingVariant] = useState<QuestionnaireNonBlockingVariant>()

  // Keyed by question so going Back and re-answering overwrites rather than double-counts.
  // A Map, not a record, so its entries stay typed; a ref because nothing renders from it.
  const answersRef = useRef(new Map<QuestionnaireQuestion, boolean>())

  const derivePhase = (): 'questionnaire' | 'sorry' | 'support' | 'booking' => {
    if (progress === 'disqualified') {
      return 'sorry'
    }
    if (progress === 'support') {
      return 'support'
    }
    if (progress === 'qualified') {
      return 'booking'
    }
    return questionnaire ? 'questionnaire' : 'booking'
  }
  const phase = derivePhase()

  const baseAnalytics = useMemo(
    () => ({
      ...(analyticsProperties ?? {
        organisation: authState.organisation?.displayName,
        organisationId: authState.organisation?.id,
        accountId: authState.user?.id
      }),
      entryPoint,
      source
    }),
    [
      analyticsProperties,
      authState.organisation?.displayName,
      authState.organisation?.id,
      authState.user?.id,
      entryPoint,
      source
    ]
  )

  const questionnaireAnalytics = { ...baseAnalytics, variant: questionnaireVariant, nonBlockingVariant }

  const handleQuestionnaireStart = (variant?: QuestionnaireNonBlockingVariant) => {
    setNonBlockingVariant(variant)
    track(GRANT_ADVISORY_QUESTIONNAIRE_EVENTS.started, { ...questionnaireAnalytics, nonBlockingVariant: variant })
  }

  const handleQuestionnaireAnswer = (question: QuestionnaireQuestion, answer: string, disqualifying: boolean) => {
    answersRef.current.set(question, disqualifying)
    track(GRANT_ADVISORY_QUESTIONNAIRE_EVENTS.answered[question], { ...questionnaireAnalytics, answer })
  }

  const handleQuestionnaireQualified = () => {
    track(GRANT_ADVISORY_QUESTIONNAIRE_EVENTS.qualified, questionnaireAnalytics)
    setProgress('qualified')
  }

  const handleQuestionnaireDisqualify = (reason: QuestionnaireQuestion) => {
    track(GRANT_ADVISORY_QUESTIONNAIRE_EVENTS.sorryShown, { ...questionnaireAnalytics, reason })
    setProgress('disqualified')
  }

  // A separate event from Sorry Shown, so support deflections don't read as ineligible orgs.
  const handleQuestionnaireSupport = (reason: QuestionnaireQuestion) => {
    track(GRANT_ADVISORY_QUESTIONNAIRE_EVENTS.supportRedirectShown, { ...questionnaireAnalytics, reason })
    setProgress('support')
  }

  const handleSupportLinkClick = (placement: SupportLinkPlacement) => {
    track(GRANT_ADVISORY_BOOKING_EVENTS.supportLinkClicked, { ...baseAnalytics, placement })
  }

  // Fire the "screen shown" event once, when the booking CTA first becomes visible.
  const bookingShownTracked = useRef(false)
  useEffect(() => {
    if (phase === 'booking' && !bookingShownTracked.current) {
      bookingShownTracked.current = true
      track(GRANT_ADVISORY_BOOKING_EVENTS.screenShown, baseAnalytics)
    }
  }, [phase, track, baseAnalytics])

  const handleBook = () => {
    const answers = [...answersRef.current]

    track(GRANT_ADVISORY_BOOKING_EVENTS.bookCallClicked, {
      ...baseAnalytics,
      url: bookingUrl,
      nonBlockingVariant,
      questionsAnswered: answers.length,
      // Empty in control. Separates a real lift from one made of unqualified bookings.
      disqualifyingAnswers: answers.filter(([, disqualifying]) => disqualifying).map(([question]) => question)
    })
    onScheduled?.()
    window.open(bookingUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <Modal
      open
      onClose={onClose}
      width={640}
      data-testid="grant-advisory-booking-modal"
      classNames={{ content: styles.modalContent }}
    >
      <Box className={styles.container}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t('grantAdvisoryBooking.close')}
          data-testid="grant-advisory-close-button"
        >
          <Icon name="close" size={4} color="gray600" />
        </button>

        {phase === 'questionnaire' && questionnaire === 'experiment' && (
          <GrantQuestionnaireExperiment
            onStart={handleQuestionnaireStart}
            onAnswer={handleQuestionnaireAnswer}
            onDisqualify={handleQuestionnaireDisqualify}
            onQualified={handleQuestionnaireQualified}
          />
        )}

        {phase === 'questionnaire' && questionnaire === 'eligibility' && (
          <GrantEligibilityQuestionnaire
            onStart={() => handleQuestionnaireStart()}
            onAnswer={handleQuestionnaireAnswer}
            onDisqualify={handleQuestionnaireDisqualify}
            onSupport={handleQuestionnaireSupport}
            onQualified={handleQuestionnaireQualified}
          />
        )}

        {phase === 'sorry' && <SorryScreen onClose={onClose} />}

        {phase === 'support' && (
          <SupportRedirectScreen
            onClose={onClose}
            onSupportLinkClick={() => handleSupportLinkClick('supportRedirect')}
          />
        )}

        {phase === 'booking' && (
          <Flex direction="column" align="center" className={styles.bookingScreen} data-testid="grant-advisory-booking">
            <div className={styles.iconBadge}>
              <Icon name="reward-stars" size={7} color="primary400" />
            </div>
            <Typography variant="h4" className={styles.bookingTitle}>
              {t('grantAdvisoryBooking.title')}
            </Typography>
            <Typography variant="default" $color="gray500" className={styles.bookingSubtitle}>
              {t('grantAdvisoryBooking.subtitle')}
            </Typography>
            {entryPoint === 'sidebar' && (
              <Box w="100%" mt={24}>
                <BookingNotice onSupportLinkClick={() => handleSupportLinkClick('booking')} />
              </Box>
            )}
            <Box w="100%" mt={entryPoint === 'sidebar' ? 24 : 32}>
              <Button variant="primary" stretch onClick={handleBook} data-testid="grant-advisory-book-button">
                {t('grantAdvisoryBooking.bookButton')}
              </Button>
            </Box>
          </Flex>
        )}
      </Box>
    </Modal>
  )
}
